import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate, useLocation } from "react-router-dom";
import { Image, Trash, Loader2, ChevronDown, TrendingDown, TrendingUp, Boxes, MoreHorizontal, ArrowUp, ArrowDown, HardHat, Wrench, Handshake, NotebookPen } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ImageViewDialog from "@/components/ImageViewDialog";
import SecureImage from "@/components/SecureImage";
import {
  SwipeableList,
  SwipeableListItem,
  TrailingActions,
  SwipeAction,
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import { toast } from "sonner";
import { entriesAPI } from "@/api/entries";
import { materialsAPI } from "@/api/materials";
import { peopleAPI } from "@/api/people";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { supabase } from "@/integrations/supabase/client";
import { getStorageBackendName } from "@/lib/storage-adapter";
import { useTranslation } from "react-i18next";
import { MonoValue } from "@/components/redesign/MonoValue";
import { cn } from "@/lib/utils";

// Get the current storage backend name for display
const storageBackend = getStorageBackendName();

interface Entry {
  id: string;
  rawType: string;
  type: string;
  date: Date;
  amount: number;
  amount_usd?: number;
  amount_eur?: number;
  amount_try_official?: number;
  amount_try_unofficial?: number;
  amount_usd_official?: number;
  amount_usd_unofficial?: number;
  amount_eur_official?: number;
  amount_eur_unofficial?: number;
  amount_gbp_official?: number;
  amount_gbp_unofficial?: number;
  amount_xau_official?: number;
  amount_xau_unofficial?: number;
  amount_btc_official?: number;
  amount_btc_unofficial?: number;
  description: string;
  person: string;
  image_url?: string; // Optional image URL
  hasInvoice?: boolean;
  creatorName?: string;
  personName?: string;
  original_currency?: string;
  original_amount?: number;
  is_official?: boolean;
  // Payment-method model
  payment_method?: string | null;
  advance_sub_method?: string | null;
  counts_toward_cost?: boolean | null;
  is_settled?: boolean | null;
  settles_entry_id?: string | null;
}

interface MaterialRecord {
  id: string;
  name: string;
  brand: string;
  quantity: string;
  unit: string;
  area: string;
  details?: string;
}

interface EntriesTabProps {
  projectId: string;
  projectStatus?: "active" | "on-track" | "delayed" | "completed";
  hideControls?: boolean;
  searchQuery?: string;
  showOnlyOfficial?: boolean;
  onlyEntryTypes?: string[];
}

// Helper function to map database entries to the Entry type
const mapDatabaseEntryToEntryType = async (dbEntry: any, t: any): Promise<Entry> => {
  // Fetch person name if person_id exists
  let personName = t("entries.unnamed");
  if (dbEntry.person_id) {
    try {
      const personData = await peopleAPI.getPerson(dbEntry.person_id);
      personName = personData.name || t("entries.unnamed");
    } catch (err) {
      
    }
  }

  // Fetch creator name if user_id exists
  let creatorName = "";
  if (dbEntry.user_id) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('name')
        .eq('id', dbEntry.user_id)
        .single();
      
      if (!error && data) {
        creatorName = data.name || "";
      }
    } catch (err) {
      
    }
  }

  // Determine if the entry is official
  const isOfficial = dbEntry.is_official || false;

  return {
    id: dbEntry.id,
    rawType: dbEntry.type,
    type: mapEntryTypeToDisplayName(dbEntry.type, t),
    date: new Date(dbEntry.date),
    amount: dbEntry.amount || 0,
    // Store all currency values directly from the database
    amount_try_official: dbEntry.amount_try_official || 0,
    amount_try_unofficial: dbEntry.amount_try_unofficial || 0,
    amount_usd_official: dbEntry.amount_usd_official || 0,
    amount_usd_unofficial: dbEntry.amount_usd_unofficial || 0,
    amount_eur_official: dbEntry.amount_eur_official || 0,
    amount_eur_unofficial: dbEntry.amount_eur_unofficial || 0,
    amount_gbp_official: dbEntry.amount_gbp_official || 0,
    amount_gbp_unofficial: dbEntry.amount_gbp_unofficial || 0,
    amount_xau_official: dbEntry.amount_xau_official || 0,
    amount_xau_unofficial: dbEntry.amount_xau_unofficial || 0,
    amount_btc_official: dbEntry.amount_btc_official || 0,
    amount_btc_unofficial: dbEntry.amount_btc_unofficial || 0,
    // Legacy fields for backward compatibility
    amount_usd: dbEntry.amount_usd || dbEntry.amount_usd_official || dbEntry.amount_usd_unofficial || 0,
    amount_eur: dbEntry.amount_eur || dbEntry.amount_eur_official || dbEntry.amount_eur_unofficial || 0,
    description: dbEntry.description || "",
    person: dbEntry.person_name || personName,
    image_url: dbEntry.file_url || dbEntry.image_url,
    hasInvoice: dbEntry.has_invoice || dbEntry.is_official || false,
    creatorName: creatorName,
    personName: personName,
    original_currency: dbEntry.original_currency || 'TRY',
    original_amount: dbEntry.original_amount || dbEntry.amount || 0,
    is_official: isOfficial,
    payment_method: dbEntry.payment_method ?? null,
    advance_sub_method: dbEntry.advance_sub_method ?? null,
    counts_toward_cost: dbEntry.counts_toward_cost ?? null,
    is_settled: dbEntry.is_settled ?? null,
    settles_entry_id: dbEntry.settles_entry_id ?? null,
  };
};

// Helper function to map entry type values to display names
const mapEntryTypeToDisplayName = (type: string, t: any): string => {
  switch (type) {
    case "expense":
      return t("entries.types.expense");
    case "givenCheck":
      return t("entries.types.given_check");
    case "receivedCheck":
      return t("entries.types.received_check");
    case "laborCost":
      return t("entries.types.labor_cost");
    case "delivery":
      return t("entries.types.delivery");
    case "sitePhoto":
      return t("entries.types.site_photo");
    case "sale":
      return t("entries.types.sale");
    default:
      return type;
  }
};

const localizeGeneratedCheckDescription = (description: string): string => {
  if (!description) return "";

  const raw = description.trim();
  const prefixMap: Array<{ regex: RegExp; label: string }> = [
    { regex: /^postponed check\s*:\s*/i, label: "Ertelenen Çek: " },
    { regex: /^paid check\s*:\s*/i, label: "Ödenen Çek: " },
    { regex: /^collected check\s*:\s*/i, label: "Tahsil Edilen Çek: " },
    { regex: /^unpaid check\s*:\s*/i, label: "Ödenemeyen Çek: " },
    { regex: /^uncollected check\s*:\s*/i, label: "Tahsil Edilemeyen Çek: " },
  ];

  for (const { regex, label } of prefixMap) {
    if (regex.test(raw)) {
      let cleaned = raw;
      while (regex.test(cleaned)) {
        cleaned = cleaned.replace(regex, "");
      }
      return `${label}${cleaned.trim()}`;
    }
  }

  return raw;
};

const EntriesTab: React.FC<EntriesTabProps> = ({
  projectId,
  projectStatus = "active",
  hideControls = false,
  searchQuery = "",
  showOnlyOfficial = false,
  onlyEntryTypes = [],
}) => {
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [viewingImage, setViewingImage] = useState<{ url: string; title: string } | null>(null);
  // States for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<Entry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [materials, setMaterials] = useState<MaterialRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [incomeOpen, setIncomeOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [openAccountOpen, setOpenAccountOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isProjectCompleted = projectStatus === "completed";

  // Function to fetch entries
  const fetchEntries = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Call the API to get entries for this project
      const data = await entriesAPI.getEntriesByProject(projectId);
      
      // Map the data to our Entry type - using Promise.all to handle async mapping
      const mappedEntriesPromises = data.map((entry: any) => mapDatabaseEntryToEntryType(entry, t));
      const mappedEntries = await Promise.all(mappedEntriesPromises);
      
      setEntries(mappedEntries);
    } catch (err) {
      
      setError(t("entries.entries_loading_error"));
      toast.error(t("entries.entries_loading_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMaterials = async () => {
    if (!projectId) return;
    try {
      const data = await materialsAPI.getMaterialsByProject(projectId);
      const mappedMaterials = data.map((material: any) => ({
        id: material.id,
        name: material.name || "",
        brand: material.brand || "",
        quantity: material.quantity?.toString() || "",
        unit: material.unit || "",
        area: material.area || "",
        details: material.details || "",
      }));
      setMaterials(mappedMaterials);
    } catch (err) {
      setMaterials([]);
    }
  };

  // Fetch entries data when component mounts or projectId changes
  useEffect(() => {
    fetchEntries();
    fetchMaterials();
  }, [projectId]);

  // Refresh entries when navigating back to this screen
  useEffect(() => {
    // This will run when component is focused (navigated back to)
    const handleFocus = () => {
      fetchEntries();
      fetchMaterials();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Also refresh when path changes (navigating back from entry detail)
  useEffect(() => {
    fetchEntries();
    fetchMaterials();
  }, [location.pathname]);


  // Function to handle viewing an image
  const handleViewImage = (url: string, title: string) => {
    setViewingImage({ url, title });
  };

  // Function to handle delete entry
  const handleDeleteClick = (entry: Entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };
  
  // Function to confirm deletion
  const confirmDelete = async () => {
    if (!entryToDelete) return;
    
    setIsDeleting(true);
    try {
      // Call the API to delete the entry
      await entriesAPI.deleteEntry(entryToDelete.id);
      
      // Update the local state after successful API call
      setEntries(prev => prev.filter(e => e.id !== entryToDelete.id));
      
      // Show success toast
      toast.success(t("entries.entry_deleted_successfully"));

      // Refresh entries after deletion to ensure everything is up to date
      fetchEntries();
    } catch (error) {
      
      toast.error(t("entries.entry_delete_error"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  };

  // Function to handle clicking on an entry (for edit)
  const handleEntryClick = (entry: Entry) => {
    // If it's a sale entry, navigate to sale page with the entry ID for editing
    if (entry.type === t("entries.types.sale")) {
      navigate(`/sale/${entry.id}`);
    } else {
      navigate(`/entry/${entry.id}`);
    }
  };

  const effectiveSearch = (hideControls ? searchQuery : filter).trim().toLowerCase();

  const filteredEntries = entries.filter((entry) => {
    const localizedDescription = localizeGeneratedCheckDescription(entry.description);
    const matchesSearch = localizedDescription
      .toLowerCase()
      .includes(effectiveSearch) || entry.person.toLowerCase().includes(effectiveSearch);
    const matchesType = typeFilter === "all" || entry.type === typeFilter;
    // Exclude site photo entries
    const isNotSitePhoto = entry.type !== t("entries.types.site_photo");
    const matchesOfficialFilter = !showOnlyOfficial || !!entry.is_official;
    const matchesAllowedTypes = onlyEntryTypes.length === 0 || onlyEntryTypes.includes(entry.rawType);
    return matchesSearch && matchesType && isNotSitePhoto && matchesOfficialFilter && matchesAllowedTypes;
  });

  const expenseTypes = new Set([
    t("entries.types.expense"),
    t("entries.types.given_check"),
    t("entries.types.labor_cost"),
  ]);
  const incomeTypes = new Set([
    t("entries.types.sale"),
    t("entries.types.received_check"),
  ]);
  // Advance (Anlaşma/Bağlantı): upfront payments + their draw-down records.
  const advanceEntries = filteredEntries.filter(
    (entry) => entry.payment_method === "advance" || entry.counts_toward_cost === false
  );
  // Açık Hesap: the debt entries + any settlement payments against them.
  const openAccountEntries = filteredEntries.filter(
    (entry) => entry.payment_method === "open_account" || !!entry.settles_entry_id
  );
  // Giderler excludes advance / açık hesap rows so they aren't shown as standard expenses.
  const expenseEntries = filteredEntries.filter(
    (entry) =>
      expenseTypes.has(entry.type) &&
      entry.payment_method !== "advance" &&
      entry.payment_method !== "open_account" &&
      entry.counts_toward_cost !== false &&
      !entry.settles_entry_id
  );
  const incomeEntries = filteredEntries.filter((entry) => incomeTypes.has(entry.type));
  const filteredMaterials = materials.filter((material) => {
    const q = effectiveSearch;
    if (!q) return true;
    return (
      material.name.toLowerCase().includes(q) ||
      material.brand.toLowerCase().includes(q) ||
      material.area.toLowerCase().includes(q) ||
      material.details?.toLowerCase().includes(q)
    );
  });

  // Expense/income tone for amount
  const getAmountTone = (type: string) => {
    if (type === t("entries.types.sale") || type === t("entries.types.received_check")) return "success" as const;
    return "error" as const;
  };

  const getEntryLeading = (type: string) => {
    if (type === t("entries.types.expense") || type === t("entries.types.given_check")) {
      return {
        icon: ArrowUp,
        bgClass: "bg-error-50",
        iconClass: "text-error-600",
      };
    }
    if (type === t("entries.types.labor_cost")) {
      return {
        icon: HardHat,
        bgClass: "bg-gray-100",
        iconClass: "text-gray-700",
      };
    }
    if (type === t("entries.types.delivery")) {
      return {
        icon: Wrench,
        bgClass: "bg-gray-100",
        iconClass: "text-gray-700",
      };
    }
    return {
      icon: ArrowDown,
      bgClass: "bg-success-50",
      iconClass: "text-success-700",
    };
  };

  const getEntryResultBadge = (entry: Entry) => {
    if (entry.type === t("entries.types.given_check")) {
      return { label: "Gecikti", className: "bg-error-50 text-error-600 border border-error-100" };
    }
    if (entry.type === t("entries.types.sale") || entry.type === t("entries.types.received_check")) {
      return { label: "Ödendi", className: "bg-success-50 text-success-700 border border-success-100" };
    }
    if (entry.is_official || entry.hasInvoice) {
      return { label: "Ödendi", className: "bg-success-50 text-success-700 border border-success-100" };
    }
    return { label: "Anlaşılan", className: "bg-gray-100 text-gray-700 border border-gray-200" };
  };

  // Render entry card
  const renderEntryCard = (entry: Entry) => {
    // Get the amount in user's preferred currency
    const getAmountInPreferredCurrency = () => {
      const baseCurrency = preferences?.base_currency || 'TRY';
      const originalCurrency = entry.original_currency || 'TRY';
      
      // If the original currency matches the user's preference, show that first
      if (originalCurrency.toUpperCase() === baseCurrency) {
        return entry.original_amount > 0 
          ? `${entry.original_amount.toLocaleString()} ${baseCurrency}` 
          : "";
      }
      
      // Get appropriate amount based on entry's official status and user preferences
      const isOfficial = entry.is_official || entry.hasInvoice;
      
      // Create a map of all currency amounts with official/unofficial distinction
      const currencyMap = {
        'TRY': {
          official: entry.amount_try_official || 0,
          unofficial: entry.amount_try_unofficial || 0,
          fallback: entry.amount // Legacy fallback
        },
        'USD': {
          official: entry.amount_usd_official || 0,
          unofficial: entry.amount_usd_unofficial || 0,
          fallback: entry.amount_usd // Legacy fallback
        },
        'EUR': {
          official: entry.amount_eur_official || 0,
          unofficial: entry.amount_eur_unofficial || 0,
          fallback: entry.amount_eur // Legacy fallback
        },
        'GBP': {
          official: entry.amount_gbp_official || 0,
          unofficial: entry.amount_gbp_unofficial || 0
        },
        'XAU': {
          official: entry.amount_xau_official || 0,
          unofficial: entry.amount_xau_unofficial || 0
        },
        'BTC': {
          official: entry.amount_btc_official || 0,
          unofficial: entry.amount_btc_unofficial || 0
        }
      };
      
      // Get currency data for the preferred base currency
      const currencyData = currencyMap[baseCurrency];
      if (!currencyData) return "";
      
      // Choose official or unofficial based on entry status
      let amount = 0;
      if (isOfficial && currencyData.official > 0) {
        amount = currencyData.official;
      } else if (!isOfficial && currencyData.unofficial > 0) {
        amount = currencyData.unofficial;
      } else if (currencyData.fallback > 0) {
        // Use fallback if specific official/unofficial not available
        amount = currencyData.fallback;
      } else {
        // Use either official or unofficial if the appropriate one is not available
        amount = currencyData.official || currencyData.unofficial || 0;
      }
      
      // If we have an amount in the preferred currency, format and return it
      if (amount > 0) {
        return `${amount.toLocaleString()} ${baseCurrency}`;
      }
      
      // Fall back to TRY if none of the above worked
      const tryAmount = currencyMap.TRY.official || currencyMap.TRY.unofficial || currencyMap.TRY.fallback || 0;
      if (tryAmount > 0 && baseCurrency !== 'TRY') {
        return `${tryAmount.toLocaleString()} TL`;
      }
      
      // If no amount available
      return "";
    };

    const localizedDescription = localizeGeneratedCheckDescription(entry.description);
    const amountStr = getAmountInPreferredCurrency();
    const leading = getEntryLeading(entry.type);
    const resultBadge = getEntryResultBadge(entry);
    const LeadingIcon = leading.icon;

    return (
      <div
        className="bg-white border border-[#e4e7ec] rounded-xl shadow-[0px_4px_4px_rgba(10,13,18,0.1),0px_2px_2px_rgba(10,13,18,0.06)] p-4 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => handleEntryClick(entry)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("flex h-9 w-9 items-center justify-center rounded-full", leading.bgClass)}>
              <LeadingIcon className={cn("h-4 w-4", leading.iconClass)} />
            </div>
            <div>
              <p className="text-[14px] font-semibold text-gray-700 leading-5">{localizedDescription}</p>
              <p className="text-xs text-gray-500">{format(entry.date, "dd/MM/yy", { locale: tr })}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {entry.image_url && (
              <button
                type="button"
                className="text-brand-600 hover:text-brand-700 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewImage(entry.image_url!, localizedDescription);
                }}
              >
                <Image className="h-3.5 w-3.5" />
              </button>
            )}
            <span
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-[11px] font-medium",
                entry.hasInvoice || entry.is_official
                  ? "border-brand-200 bg-brand-50 text-brand-600"
                  : "border-gray-200 bg-gray-100 text-gray-700",
              )}
            >
              {entry.hasInvoice || entry.is_official ? "Resmi" : "Gayriresmi"}
            </span>
            <MoreHorizontal className="h-4 w-4 text-gray-400" />
          </div>
        </div>

        <div className="mt-3 flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-gray-600 truncate">
              {entry.personName && entry.personName !== t("entries.unnamed") ? entry.personName : entry.person}
            </p>
            <p className="mt-0.5 text-[12px] text-[#8d9fbc] truncate">{entry.type}</p>
            {entry.payment_method === "open_account" && (
              <span className="mt-1 inline-block rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                {entry.is_settled ? "Açık Hesap · Ödendi" : "Açık Hesap · Ödenmedi"}
              </span>
            )}
            {entry.payment_method === "advance" && (
              <span className="mt-1 inline-block rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                Avans
              </span>
            )}
            {entry.payment_method !== "advance" && entry.counts_toward_cost === false && (
              <span className="mt-1 inline-block rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                Avanstan düşüldü
              </span>
            )}
            {entry.settles_entry_id && (
              <span className="mt-1 inline-block rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                Açık hesap ödemesi
              </span>
            )}
            {entry.creatorName && (
              <p className="mt-1 text-[12px] text-gray-400 truncate">{entry.creatorName}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            {amountStr && (
              <span className={cn(
                "font-mono text-[20px] font-medium tabular-nums leading-none",
                getAmountTone(entry.type) === "success" ? "text-success-600" : "text-error-600"
              )}>
                {amountStr}
              </span>
            )}
            {!amountStr.includes('TL') &&
              ((entry.is_official && entry.amount_try_official > 0) ||
                (!entry.is_official && entry.amount_try_unofficial > 0) ||
                entry.amount > 0) && (
                <MonoValue
                  value={`${((entry.is_official && entry.amount_try_official) || (!entry.is_official && entry.amount_try_unofficial) || entry.amount).toLocaleString()} TL`}
                  tone="muted" size="meta" className="block"
                />
              )}
            <span className={cn("mt-1 inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium", resultBadge.className)}>
              {resultBadge.label}
            </span>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-center gap-2 border-t border-gray-100 pt-2 text-[11px] font-semibold text-gray-500">
          <span>İlgili kayıtlar</span>
          <ChevronDown className="h-3.5 w-3.5" />
        </div>
      </div>
    );
  };

  return (
    <div className="pt-2">
      {!hideControls && (
        <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-3 mb-4">
          <div className="flex-1">
            <Input
              placeholder={t("entries.search_placeholder")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full border-gray-200 rounded-lg bg-white"
            />
          </div>
          <div className="w-full md:w-40">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="border-gray-200 rounded-lg bg-white">
                <SelectValue placeholder={t("entries.all_types")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("entries.all_types")}</SelectItem>
                <SelectItem value={t("entries.types.expense")}>{t("entries.types.expense")}</SelectItem>
                <SelectItem value={t("entries.types.given_check")}>{t("entries.types.given_check")}</SelectItem>
                <SelectItem value={t("entries.types.received_check")}>{t("entries.types.received_check")}</SelectItem>
                <SelectItem value={t("entries.types.labor_cost")}>{t("entries.types.labor_cost")}</SelectItem>
                <SelectItem value={t("entries.types.delivery")}>{t("entries.types.delivery")}</SelectItem>
                <SelectItem value={t("entries.types.sale")}>{t("entries.types.sale")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            className="bg-brand-600 hover:bg-brand-700 px-4"
            onClick={() => navigate("/new-entry")}
            disabled={isProjectCompleted}
            title={isProjectCompleted ? t("entries.completed_project_button_tooltip") : ""}
          >
            {t("entries.new_entry")}
          </Button>
        </div>
      )}

      {isProjectCompleted && (
        <div className="mb-4 bg-warning-50 border border-warning-300 text-warning-600 rounded-xl px-4 py-2 text-sm">
          {t("entries.completed_project_warning")}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-brand-600" />
            <p className="text-gray-500">{t("entries.loading_entries")}</p>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-8 text-error-600">
          <p>{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            {t("entries.retry")}
          </Button>
        </div>
      ) : hideControls ? (
        <div className="space-y-4 pb-2">
          <div className="rounded-[10px] border border-[#f2f6fa] bg-white shadow-sm">
            <button
              type="button"
              className="flex w-full items-center justify-between px-[14px] py-[10px]"
              onClick={() => setExpenseOpen((prev) => !prev)}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#fff8f7]">
                  <TrendingDown className="h-4 w-4 text-[#ff897d]" />
                </div>
                <div className="text-left">
                  <p className="text-[18px] font-bold text-[#414651]">Giderler</p>
                  <p className="text-xs text-[#414651]">{expenseEntries.length} işlem</p>
                </div>
              </div>
              <ChevronDown className={cn("h-4 w-4 text-[#414651] transition-transform", expenseOpen && "rotate-180")} />
            </button>
            {expenseOpen && (
              <div className="space-y-3 px-2 pb-2">
                {expenseEntries.length > 0 ? expenseEntries.map((entry) => (
                  <div className="relative w-full" key={entry.id}>
                    {renderEntryCard(entry)}
                  </div>
                )) : (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs text-gray-500">
                    Kayıt bulunamadı
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Avans (Anlaşma/Bağlantı) */}
          <div className="rounded-[10px] border border-[#f2f6fa] bg-white shadow-sm">
            <button
              type="button"
              className="flex w-full items-center justify-between px-[14px] py-[10px]"
              onClick={() => setAdvanceOpen((prev) => !prev)}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#ecfdf3]">
                  <Handshake className="h-4 w-4 text-[#067647]" />
                </div>
                <div className="text-left">
                  <p className="text-[18px] font-bold text-[#414651]">Avans (Anlaşma/Bağlantı)</p>
                  <p className="text-xs text-[#414651]">{advanceEntries.length} işlem</p>
                </div>
              </div>
              <ChevronDown className={cn("h-4 w-4 text-[#414651] transition-transform", advanceOpen && "rotate-180")} />
            </button>
            {advanceOpen && (
              <div className="space-y-3 px-2 pb-2">
                {advanceEntries.length > 0 ? advanceEntries.map((entry) => (
                  <div className="relative w-full" key={entry.id}>
                    {renderEntryCard(entry)}
                  </div>
                )) : (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs text-gray-500">
                    Kayıt bulunamadı
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Açık Hesap */}
          <div className="rounded-[10px] border border-[#f2f6fa] bg-white shadow-sm">
            <button
              type="button"
              className="flex w-full items-center justify-between px-[14px] py-[10px]"
              onClick={() => setOpenAccountOpen((prev) => !prev)}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#fffaeb]">
                  <NotebookPen className="h-4 w-4 text-[#f79009]" />
                </div>
                <div className="text-left">
                  <p className="text-[18px] font-bold text-[#414651]">Açık Hesap</p>
                  <p className="text-xs text-[#414651]">{openAccountEntries.length} işlem</p>
                </div>
              </div>
              <ChevronDown className={cn("h-4 w-4 text-[#414651] transition-transform", openAccountOpen && "rotate-180")} />
            </button>
            {openAccountOpen && (
              <div className="space-y-3 px-2 pb-2">
                {openAccountEntries.length > 0 ? openAccountEntries.map((entry) => (
                  <div className="relative w-full" key={entry.id}>
                    {renderEntryCard(entry)}
                  </div>
                )) : (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs text-gray-500">
                    Kayıt bulunamadı
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-[10px] border border-[#f2f6fa] bg-white shadow-sm">
            <button
              type="button"
              className="flex w-full items-center justify-between px-[14px] py-[10px]"
              onClick={() => setIncomeOpen((prev) => !prev)}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#f6fef9]">
                  <TrendingUp className="h-4 w-4 text-[#067647]" />
                </div>
                <div className="text-left">
                  <p className="text-[18px] font-bold text-[#414651]">Gelirler</p>
                  <p className="text-xs text-[#414651]">{incomeEntries.length} işlem</p>
                </div>
              </div>
              <ChevronDown className={cn("h-4 w-4 text-[#414651] transition-transform", incomeOpen && "rotate-180")} />
            </button>
            {incomeOpen && (
              <div className="space-y-3 px-2 pb-2">
                {incomeEntries.length > 0 ? incomeEntries.map((entry) => (
                  <div className="relative w-full" key={entry.id}>
                    {renderEntryCard(entry)}
                  </div>
                )) : (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs text-gray-500">
                    Kayıt bulunamadı
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-[10px] border border-[#f2f6fa] bg-white shadow-sm">
            <button
              type="button"
              className="flex w-full items-center justify-between px-[14px] py-[10px]"
              onClick={() => setMaterialsOpen((prev) => !prev)}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#f6fef9]">
                  <Boxes className="h-4 w-4 text-[#f79009]" />
                </div>
                <div className="text-left">
                  <p className="text-[18px] font-bold text-[#414651]">Malzemeler</p>
                  <p className="text-xs text-[#414651]">{filteredMaterials.length} malzeme</p>
                </div>
              </div>
              <ChevronDown className={cn("h-4 w-4 text-[#414651] transition-transform", materialsOpen && "rotate-180")} />
            </button>
            {materialsOpen && (
              <div className="space-y-3 px-2 pb-2">
                {filteredMaterials.length > 0 ? filteredMaterials.map((material) => (
                  <div
                    key={material.id}
                    className="rounded-xl border border-[#e4e7ec] bg-white p-4 shadow-[0px_4px_4px_rgba(10,13,18,0.1),0px_2px_2px_rgba(10,13,18,0.06)]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-semibold text-gray-700 leading-5">{material.name}</p>
                        <p className="mt-1 text-xs text-gray-500">{material.brand}</p>
                        {material.details && <p className="mt-1 text-xs text-gray-500">{material.details}</p>}
                      </div>
                      {material.area && (
                        <span className="shrink-0 rounded-md border border-brand-100 bg-brand-50 px-2 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-600">
                          {material.area}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 border-t border-gray-100 pt-2">
                      <p className="font-mono text-sm text-gray-800">
                        {material.quantity} {material.unit}
                      </p>
                    </div>
                  </div>
                )) : (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs text-gray-500">
                    Kayıt bulunamadı
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredEntries.length > 0 ? (
            <div className="space-y-3">
              {filteredEntries.map((entry) => (
                <div className="relative w-full" key={entry.id}>
                  {renderEntryCard(entry)}
                  {!hideControls && (
                    <button
                      className="absolute top-3 right-3 text-gray-400 hover:text-error-600 transition-colors z-20"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(entry);
                      }}
                      aria-label={t("entries.delete_entry")}
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              {t("entries.no_entries_found")}
            </div>
          )}
        </div>
      )}

      {/* Image Viewing Dialog */}
      {viewingImage && (
        <ImageViewDialog
          imageUrl={viewingImage.url}
          title={viewingImage.title}
          open={!!viewingImage}
          onOpenChange={(open) => {
            if (!open) setViewingImage(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t("entries.delete_entry")}
        description={t("entries.delete_entry_confirmation")}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default EntriesTab;
